import { useFeatureFlag } from '@agentnet/feature-flags';
import { MarketplaceProduct } from 'api/admin/types';
import { propertyType } from 'api/file/interfaces/get-file';
import { getTitleMonitoringInviteByPropertyId } from 'api/titleMonitoring/titleMonitoring-api';
import { TitleMonitoringInvite } from 'api/titleMonitoring/types';
import { TITLE_MONITORING_PRODUCT_CODE } from 'features/help/constants';
import { useMarketPlace } from 'hooks/useMarketPlace';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FeatureFlag } from 'utilities/constants/launch-darkly-flags';

export interface TitleMonitoringInvites {
  invitesByPropertyId: Record<string, TitleMonitoringInvite>;
  isTitleMonitoringAvailable: boolean;
  isLoadingInvites: boolean;
  refreshInvites: () => void;
}

/* propertyId is a string on the file property but a number on the invite, so both are keyed on one canonical form */
export const getTitleMonitoringInviteKey = (propertyId: string | number | undefined): string | undefined => {
  const numericPropertyId = Number(propertyId);
  return Number.isFinite(numericPropertyId) && numericPropertyId > 0 ? String(numericPropertyId) : undefined;
};

export const TITLE_MONITORING_COUNTY_EXCLUDED_MESSAGE = 'Title Monitoring is not available in this county.';
export const TITLE_MONITORING_PROPERTY_UNVERIFIED_MESSAGE =
  'Validate the property address, then save the file to check Title Monitoring eligibility.';

export const isTitleMonitoringInviteEligible = (invite: TitleMonitoringInvite | null | undefined): boolean => {
  if (!invite) {
    return false;
  }

  if (invite.isCountyExcluded) {
    return false;
  }

  /* Typed as a string but returned as a number, so it is coerced before the blank check */
  return String(invite.propertyDetailId ?? '').trim().length > 0;
};

export const getTitleMonitoringDisabledReason = (
  invite: TitleMonitoringInvite | null | undefined,
): string | undefined => {
  if (isTitleMonitoringInviteEligible(invite)) {
    return undefined;
  }

  return invite?.isCountyExcluded
    ? TITLE_MONITORING_COUNTY_EXCLUDED_MESSAGE
    : TITLE_MONITORING_PROPERTY_UNVERIFIED_MESSAGE;
};

export const useTitleMonitoringInvites = (properties: propertyType[]): TitleMonitoringInvites => {
  const { enabled: isTitleMonitoringEnabled, ready: isTitleMonitoringFlagReady } = useFeatureFlag(
    FeatureFlag.TITLE_MONITORING_ENABLED,
    false,
  );
  const { fetchMarketPlaceServices, fetchMarketPlaceServicesResults } = useMarketPlace();

  const monitorablePropertyIds = useMemo(
    () =>
      properties
        .map((property) => getTitleMonitoringInviteKey(property.propertyId))
        .filter((propertyId): propertyId is string => propertyId !== undefined),
    [properties],
  );
  const hasMonitorableProperties = monitorablePropertyIds.length > 0;
  /* properties is rebuilt on every file-data sync, so the effect keys off the ids themselves rather than array identity */
  const monitorablePropertyIdsKey = monitorablePropertyIds.join(',');
  const isTitleMonitoringFlagActive = isTitleMonitoringFlagReady && isTitleMonitoringEnabled;

  const titleMonitoringProduct = useMemo(
    () =>
      fetchMarketPlaceServicesResults?.find(
        (product: MarketplaceProduct) => product.productCode === TITLE_MONITORING_PRODUCT_CODE,
      ),
    [fetchMarketPlaceServicesResults],
  );
  const isTitleMonitoringAvailable = Boolean(titleMonitoringProduct?.isEnabled);

  const [invitesByPropertyId, setInvitesByPropertyId] = useState<Record<string, TitleMonitoringInvite>>({});
  const [isLoadingInvites, setIsLoadingInvites] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);
  const hasFetchedProductsRef = useRef(false);
  const fetchedIdsRef = useRef(new Set<string>());
  const isMountedRef = useRef(true);
  const latestRefreshTokenRef = useRef(0);

  const refreshInvites = useCallback(() => {
    fetchedIdsRef.current.clear();
    latestRefreshTokenRef.current += 1;
    setRefreshToken(latestRefreshTokenRef.current);
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!hasMonitorableProperties || !isTitleMonitoringFlagActive || hasFetchedProductsRef.current) {
      return;
    }

    hasFetchedProductsRef.current = true;
    fetchMarketPlaceServices();
  }, [hasMonitorableProperties, isTitleMonitoringFlagActive, fetchMarketPlaceServices]);

  useEffect(() => {
    if (!hasMonitorableProperties || !isTitleMonitoringFlagActive || !isTitleMonitoringAvailable) {
      return;
    }

    const propertyIdsToFetch = monitorablePropertyIds.filter((propertyId) => !fetchedIdsRef.current.has(propertyId));

    if (propertyIdsToFetch.length === 0) {
      return;
    }

    propertyIdsToFetch.forEach((propertyId) => {
      fetchedIdsRef.current.add(propertyId);
    });

    setIsLoadingInvites(true);

    const requestRefreshToken = refreshToken;

    Promise.all(
      propertyIdsToFetch.map(async (propertyId) => {
        const invite = await getTitleMonitoringInviteByPropertyId(Number(propertyId));
        return { propertyId, invite };
      }),
    ).then((results) => {
      /* A properties re-sync must not discard an in-flight result already marked as fetched, but a result from a
         superseded refresh must be dropped so it cannot overwrite what the newer refresh already stored */
      if (!isMountedRef.current || requestRefreshToken !== latestRefreshTokenRef.current) {
        return;
      }

      setInvitesByPropertyId((previousInvites) => {
        const nextInvites = { ...previousInvites };
        results.forEach(({ propertyId, invite }) => {
          if (invite) {
            nextInvites[propertyId] = invite;
          } else {
            delete nextInvites[propertyId];
          }
        });
        return nextInvites;
      });
      setIsLoadingInvites(false);
    });
  }, [
    hasMonitorableProperties,
    isTitleMonitoringFlagActive,
    isTitleMonitoringAvailable,
    monitorablePropertyIdsKey,
    refreshToken,
  ]);

  /* The effect that flips isLoadingInvites runs after commit, so unfetched ids also count as pending
     to keep callers from reading an empty map as a resolved "not eligible" answer */
  const hasUnrequestedInvites =
    isTitleMonitoringAvailable && monitorablePropertyIds.some((propertyId) => !fetchedIdsRef.current.has(propertyId));

  return {
    invitesByPropertyId,
    isTitleMonitoringAvailable,
    isLoadingInvites: isLoadingInvites || hasUnrequestedInvites,
    refreshInvites,
  };
};
